import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Star, Eye, EyeOff, BookOpen } from 'lucide-react';
import { useApiCaller } from '../../utils/ApiUtilities';
import MathText from '../ui/MathText';
import {
    getVocabWordsUrl,
    getVocabQuestionsUrl,
    getFavoriteWordsUrl,
    getFavoriteWordPostUrl,
    getFavoriteWordDeleteUrl,
    getFavoriteWordStatusUrl,
} from '../../utils/UrlUtilities';
import { constructFavoriteWordStatusPayload } from '../../utils/PayloadUtils';
import { VOCAB_EXAM_TABS, VOCAB_VIEWS, VOCAB_GRE_SOURCE_TABS, vocabHasSources, VOCAB_ALPHABET } from '../../utils/Constants';
import '../components.css';

// Vocabulary Store: exam tabs (GRE / IELTS-TOEFL-SAT / General). Every tab shows the same three
// views — Your Flash Cards, Test Your Skill, Your Favorites. GRE additionally splits each view into
// two book-source sub-tabs (Barron's / Manhattan); other tabs have no source. Flash cards +
// favorites are a word-list (left) → stylish flashcard (right) layout; practice reveals answers
// with no scoring.

const PURPLE_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

const tabStyle = (isActive, isHovered) => {
    const base = {
        border: 'none', borderRadius: '0.7rem', padding: '0.55rem 1.15rem', fontWeight: 700,
        fontSize: '0.95rem', cursor: 'pointer',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease', outline: 'none',
    };
    if (isActive) {
        return { ...base, background: PURPLE_GRADIENT, color: '#ffffff', transform: 'translateY(-2px)',
            boxShadow: '0 8px 16px -5px rgba(124,58,237,0.55), inset 0 1px 0 rgba(255,255,255,0.35)' };
    }
    return { ...base,
        background: isHovered ? 'linear-gradient(145deg,#ede9fe,#ddd6fe)' : 'linear-gradient(145deg,#faf5ff,#ede9fe)',
        color: '#5b21b6', transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: isHovered
            ? '0 6px 14px -6px rgba(91,33,182,0.45), inset 0 1px 0 rgba(255,255,255,0.8)'
            : '0 2px 5px -2px rgba(91,33,182,0.3), inset 0 1px 0 rgba(255,255,255,0.8)' };
};

// GRE book-source sub-tabs (Barron's / Manhattan) — a lighter, smaller segmented pill pair so the
// hierarchy reads exam-tab → view-button → source-pill. Active pill is the purple gradient.
const sourceTabStyle = (isActive, isHovered) => {
    const base = {
        border: '1px solid #ddd6fe', borderRadius: '9999px', padding: '0.4rem 1rem', fontWeight: 600,
        fontSize: '0.85rem', cursor: 'pointer',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease', outline: 'none',
    };
    if (isActive) {
        return { ...base, background: PURPLE_GRADIENT, color: '#ffffff', borderColor: 'transparent',
            transform: 'translateY(-1px)', boxShadow: '0 6px 12px -5px rgba(124,58,237,0.5)' };
    }
    return { ...base, background: isHovered ? '#f5f3ff' : '#ffffff', color: '#6d28d9',
        boxShadow: isHovered ? '0 3px 8px -4px rgba(91,33,182,0.35)' : 'none' };
};

// "Go to Word" A–Z jump filter, shown above every view. `extra` renders inline on the same row
// (e.g. the flashcard "Show Bangla Meaning" toggle).
function GoToWord({ onPick, extra }) {
    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2" style={{ paddingBottom: '0.5rem', borderBottom: '1px solid #ede9fe' }}>
            <div className="d-flex align-items-center gap-2">
                <label className="mb-0 fw-semibold" style={{ color: '#5b21b6' }}>Go to Word:</label>
                <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    defaultValue="all"
                    onChange={(e) => onPick(e.target.value)}
                >
                    <option value="all">All</option>
                    {VOCAB_ALPHABET.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
            </div>
            {extra}
        </div>
    );
}

// Case-insensitive A–Z sort by a text accessor.
const byText = (get) => (a, b) => String(get(a) || '').localeCompare(String(get(b) || ''), undefined, { sensitivity: 'base' });

// First ALPHABETIC character (uppercased) of a string — skips leading quotes/digits/spaces so a
// question like «"Aberrant" most nearly means…» jumps under "A", not a non-letter.
const firstAlpha = (s) => {
    const m = String(s || '').match(/[A-Za-z]/);
    return m ? m[0].toUpperCase() : '';
};

// The vocabulary word a practice MCQ asks about — used to sort/jump ("Go to Word") and to
// highlight it. Tries a quoted word, then the word after "meaning of", then the last word.
const extractTargetWord = (text) => {
    const t = String(text || '');
    const quoted = t.match(/["'“”‘’]([A-Za-z][A-Za-z\- ]*?)["'“”‘’]/);
    if (quoted) return quoted[1].trim();
    const after = t.match(/meaning of\s+([A-Za-z][A-Za-z-]*)/i);
    if (after) return after[1].trim();
    const words = t.replace(/[^A-Za-z\s]/g, ' ').trim().split(/\s+/).filter(Boolean);
    return words.length ? words[words.length - 1] : '';
};

// Render a practice-MCQ prompt: the asked word bold + double-quoted, the rest as normal text.
const renderPrompt = (text, word) => {
    const t = String(text || '');
    if (!word) return t;
    const i = t.toLowerCase().indexOf(word.toLowerCase());
    if (i === -1) return t;
    const before = t.slice(0, i).replace(/["'“”‘’]\s*$/, '');
    const after = t.slice(i + word.length).replace(/^\s*["'“”‘’]/, '');
    return (<>{before}<strong>&quot;{t.substr(i, word.length)}&quot;</strong>{after}</>);
};

// ---------- Stylish flashcard (right pane) ----------
function FlashCard({ word, showBangla }) {
    if (!word) {
        return (
            <div style={{ flex: 1, marginTop: '2.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', minHeight: 320 }}>
                <div className="text-center"><BookOpen size={40} style={{ opacity: 0.4 }} /><div className="mt-2">Select a word to see its card.</div></div>
            </div>
        );
    }
    const chips = (label, arr) => (arr && arr.length > 0) ? (
        <div className="mb-2">
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6d28d9', fontWeight: 700 }}>{label}</span>
            <div className="d-flex flex-wrap gap-2 mt-1">
                {arr.map((s, i) => <span key={i} className="badge rounded-pill" style={{ background: '#ede9fe', color: '#4338ca', fontWeight: 600 }}>{s}</span>)}
            </div>
        </div>
    ) : null;
    return (
        <div style={{ flex: 1, minWidth: 0, marginTop: '2.75rem' }}>
            <div style={{ background: 'linear-gradient(160deg,#ffffff,#faf5ff)', border: '1px solid #e9d5ff', borderRadius: '1rem', boxShadow: '0 12px 30px -14px rgba(91,33,182,0.45)', overflow: 'hidden' }}>
                <div style={{ background: PURPLE_GRADIENT, color: '#fff', padding: '1.1rem 1.35rem' }}>
                    <div>
                        <div style={{ fontSize: '1.9rem', fontWeight: 800, lineHeight: 1.1 }}>{word.word}</div>
                        <div style={{ opacity: 0.9, marginTop: 4 }}>
                            {word.part_of_speech && <em>{word.part_of_speech}</em>}
                            {word.pronunciation && <span style={{ marginLeft: 8, opacity: 0.85 }}>/{word.pronunciation}/</span>}
                        </div>
                    </div>
                </div>
                <div style={{ padding: '1.25rem 1.35rem' }}>
                    {word.meaning && (
                        <div className="mb-3">
                            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6d28d9', fontWeight: 700 }}>Meaning</span>
                            <div style={{ fontSize: '1.05rem', color: '#1f2937', marginTop: 2 }}><MathText text={word.meaning} /></div>
                        </div>
                    )}
                    {showBangla && word.bangla_meaning && (
                        <div className="mb-3">
                            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6d28d9', fontWeight: 700 }}>বাংলা অর্থ</span>
                            <div style={{ fontSize: '1.05rem', color: '#1f2937', marginTop: 2 }}>{word.bangla_meaning}</div>
                        </div>
                    )}
                    {word.example && (
                        <div className="mb-3">
                            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6d28d9', fontWeight: 700 }}>Example</span>
                            <div style={{ fontStyle: 'italic', color: '#374151', marginTop: 2, borderLeft: '3px solid #ddd6fe', paddingLeft: '0.75rem' }}><MathText text={word.example} /></div>
                        </div>
                    )}
                    {chips('Synonyms', word.synonyms)}
                    {chips('Antonyms', word.antonyms)}
                </div>
            </div>
        </div>
    );
}

// ---------- Flash cards / Favorites: word list (left) + flashcard (right) ----------
function WordListView({ exam, source, favoritesOnly }) {
    const CallApi = useApiCaller();
    const [words, setWords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [favMap, setFavMap] = useState({}); // { word_id: bool }
    const [busyId, setBusyId] = useState(null);
    const [showBangla, setShowBangla] = useState(false); // "Show Bangla Meaning" toggle (default off)

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (favoritesOnly) {
                const res = await CallApi({ url: getFavoriteWordsUrl({ exam, source }), method: 'GET', token: true });
                const favs = res.success ? (res.data.favorites || []) : [];
                const list = favs.map(f => f.word).filter(Boolean).sort(byText(w => w.word));
                setWords(list);
                setFavMap(Object.fromEntries(list.map(w => [w.id, true])));
                setSelectedId(list.length ? list[0].id : null);
            } else {
                const res = await CallApi({ url: getVocabWordsUrl({ exam, source }), method: 'GET', token: true });
                const list = (res.success ? (res.data.words || []) : []).slice().sort(byText(w => w.word));
                setWords(list);
                setSelectedId(list.length ? list[0].id : null);
                if (list.length) {
                    const st = await CallApi({
                        url: getFavoriteWordStatusUrl(), method: 'POST',
                        payload: constructFavoriteWordStatusPayload(list.map(w => w.id)), token: true,
                    });
                    setFavMap(st.success ? (st.data.word_ids || {}) : {});
                } else {
                    setFavMap({});
                }
            }
        } finally {
            setLoading(false);
        }
    }, [CallApi, exam, source, favoritesOnly]);

    useEffect(() => { load(); }, [load]);

    const toggleFavorite = useCallback(async (word) => {
        setBusyId(word.id);
        try {
            const isFav = !!favMap[word.id];
            if (isFav) {
                await CallApi({ url: getFavoriteWordDeleteUrl([word.id]), method: 'DELETE', token: true });
                setFavMap(m => ({ ...m, [word.id]: false }));
                if (favoritesOnly) {
                    // Drop it from the favorites list immediately.
                    setWords(prev => {
                        const next = prev.filter(w => w.id !== word.id);
                        if (selectedId === word.id) setSelectedId(next.length ? next[0].id : null);
                        return next;
                    });
                }
            } else {
                await CallApi({ url: getFavoriteWordPostUrl(), method: 'POST', payload: { word_id: word.id }, token: true });
                setFavMap(m => ({ ...m, [word.id]: true }));
            }
        } finally {
            setBusyId(null);
        }
    }, [CallApi, favMap, favoritesOnly, selectedId]);

    const selected = useMemo(() => words.find(w => w.id === selectedId) || null, [words, selectedId]);

    const jumpToLetter = useCallback((letter) => {
        if (letter === 'all') return;
        const target = words.find(w => firstAlpha(w.word) === letter);
        if (!target) return;
        setSelectedId(target.id);
        // Scroll the matching row to the TOP of the list (not just barely into view).
        setTimeout(() => {
            const el = document.getElementById(`vw-item-${target.id}`);
            if (el) el.scrollIntoView({ block: 'start' });
        }, 0);
    }, [words]);

    if (loading) return <div className="text-muted p-4">Loading words…</div>;
    if (!words.length) {
        return <div className="text-muted text-center p-5">{favoritesOnly ? 'No favorites yet. Star a word from the flash cards.' : 'No words available yet.'}</div>;
    }
    return (
        <div className="d-flex gap-3" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 48%', maxWidth: '48%' }}>
                {/* Header aligned to the left word-list column: Go to Word (left) + Show Bangla (right). */}
                <GoToWord
                    onPick={jumpToLetter}
                    extra={
                        <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: 'pointer', fontWeight: 600, color: '#5b21b6' }}>
                            <input type="checkbox" checked={showBangla} onChange={(e) => setShowBangla(e.target.checked)} />
                            Show Bangla Meaning
                        </label>
                    }
                />
                <div style={{ maxHeight: '74vh', overflowY: 'auto', paddingRight: 6 }}>
                    {words.map(w => {
                        const active = w.id === selectedId;
                        const fav = !!favMap[w.id];
                        return (
                            <div
                                key={w.id}
                                id={`vw-item-${w.id}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedId(w.id)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedId(w.id); }}
                                className={`vocab-word-row${active ? ' active' : ''}`}
                            >
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.word}</span>
                                <button
                                    type="button"
                                    title={fav ? 'Remove from favorites' : 'Add to favorites'}
                                    disabled={busyId === w.id}
                                    onClick={(e) => { e.stopPropagation(); toggleFavorite(w); }}
                                    style={{ background: 'transparent', border: 'none', padding: 0, lineHeight: 0, cursor: 'pointer', flexShrink: 0 }}
                                >
                                    <Star size={18} fill={fav ? '#fbbf24' : 'transparent'} color={fav ? '#fbbf24' : (active ? '#ffffff' : '#c4b5fd')} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
            <FlashCard word={selected} showBangla={showBangla} />
        </div>
    );
}

// ---------- Practice (reveal answer, no scoring) ----------
const OPT_LETTERS = ['A', 'B', 'C', 'D'];
function PracticeView({ exam, source }) {
    const CallApi = useApiCaller();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revealed, setRevealed] = useState({}); // { question_id: bool }
    const [picked, setPicked] = useState({});     // { question_id: 'A'|'B'|... } — user's selection

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const res = await CallApi({ url: getVocabQuestionsUrl({ exam, source }), method: 'GET', token: true });
                // Sort A–Z by the vocabulary word the question asks about.
                const list = (res.success ? (res.data.questions || []) : []).slice()
                    .sort(byText(q => extractTargetWord(q.question_text)));
                if (alive) setQuestions(list);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [CallApi, exam, source]);

    if (loading) return <div className="text-muted p-4">Loading practice questions…</div>;
    if (!questions.length) return <div className="text-muted text-center p-5">No practice questions available yet.</div>;

    const jumpToLetter = (letter) => {
        if (letter === 'all') return;
        const target = questions.find(q => firstAlpha(extractTargetWord(q.question_text)) === letter);
        if (target) {
            const el = document.getElementById(`vq-${target.id}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
            <GoToWord onPick={jumpToLetter} />
            {/* Fixed-height scroll box: only the question list scrolls, header stays put. */}
            <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto', paddingRight: 8 }}>
            {questions.map((q, i) => {
                const show = !!revealed[q.id];
                const correctLetters = (q.correct_option || '').toUpperCase().split(',').map(s => s.trim()).filter(Boolean);
                return (
                    <div key={q.id} id={`vq-${q.id}`} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.6rem', padding: '1rem 1.15rem', marginBottom: '1rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', scrollMarginTop: '8px' }}>
                        <div style={{ color: '#1f2937', marginBottom: '0.6rem' }}>
                            <span style={{ color: '#7c3aed', marginRight: 6, fontWeight: 700 }}>{i + 1}.</span>
                            {renderPrompt(q.question_text, extractTargetWord(q.question_text))}
                        </div>
                        {OPT_LETTERS.map(letter => {
                            const val = q[`option_${letter.toLowerCase()}`];
                            if (!val) return null;
                            const isCorrect = show && correctLetters.includes(letter);
                            const isPicked = picked[q.id] === letter;
                            // Revealed-correct (green) wins; otherwise the user's picked option is purple.
                            const cls = `vocab-opt${isCorrect ? ' correct' : (isPicked ? ' selected' : '')}`;
                            return (
                                <button
                                    key={letter}
                                    type="button"
                                    className={cls}
                                    onClick={() => setPicked(p => ({ ...p, [q.id]: letter }))}
                                >
                                    <strong style={{ marginRight: 6 }}>{letter}.</strong>
                                    <MathText text={val} />
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            className="btn btn-sm mt-2"
                            onClick={() => setRevealed(r => ({ ...r, [q.id]: !r[q.id] }))}
                            style={{ background: show ? '#fef3c7' : 'linear-gradient(145deg,#f59e0b,#d97706)', color: show ? '#92400e' : '#ffffff', fontWeight: 600 }}
                        >
                            {show ? <><EyeOff size={15} style={{ marginRight: 6 }} />Hide answer</> : <><Eye size={15} style={{ marginRight: 6 }} />Show answer</>}
                        </button>
                        {show && (
                            <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '0.4rem' }}>
                                <div><strong>Correct:</strong> {correctLetters.join(', ') || '—'}</div>
                                {q.answer_explanation && (
                                    <div style={{ marginTop: 4, color: '#374151' }}><strong>Explanation:</strong> <MathText text={q.answer_explanation} /></div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
            </div>
        </div>
    );
}

export default function VocabularyStore() {
    const [examTab, setExamTab] = useState('gre');
    const [hoveredTab, setHoveredTab] = useState(null);
    const [viewKey, setViewKey] = useState(VOCAB_VIEWS[0].key);
    const [sourceKey, setSourceKey] = useState('barrons'); // GRE-only book source; ignored otherwise
    const [hoveredSource, setHoveredSource] = useState(null);

    const tab = VOCAB_EXAM_TABS.find(t => t.value === examTab) || VOCAB_EXAM_TABS[0];
    const apiExam = tab.apiExam;
    const view = VOCAB_VIEWS.find(v => v.key === viewKey) || VOCAB_VIEWS[0];

    // GRE is the only tab with book sources; other tabs send no source.
    const hasSources = vocabHasSources(examTab);
    const source = hasSources ? sourceKey : '';
    const sourceTabs = hasSources ? (VOCAB_GRE_SOURCE_TABS[view.mode] || []) : null;

    const pickExam = (value) => {
        setExamTab(value);
        setViewKey(VOCAB_VIEWS[0].key); // reset to the first view
        setSourceKey('barrons');        // reset to the first book source
    };

    return (
        <div style={{ padding: '2rem', minHeight: '100vh', backgroundColor: '#f9fafb' }}>
            <h1 style={{ fontSize: '2.2rem', fontWeight: 700, color: '#1f2937', marginBottom: '1.5rem', textAlign: 'center' }}>
                Vocabulary Store
            </h1>

            {/* Exam tabs */}
            <div className="d-flex flex-wrap gap-2 mb-4 p-2" style={{ background: 'linear-gradient(145deg,#f5f3ff,#efeafe)', borderRadius: '0.9rem', justifyContent: 'center' }}>
                {VOCAB_EXAM_TABS.map(t => (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => pickExam(t.value)}
                        onMouseEnter={() => setHoveredTab(t.value)}
                        onMouseLeave={() => setHoveredTab(null)}
                        style={tabStyle(examTab === t.value, hoveredTab === t.value)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* View-switcher buttons — the same three views for every tab. */}
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: hasSources ? '1rem' : '2rem' }}>
                {VOCAB_VIEWS.map(v => (
                    <button
                        key={v.key}
                        onClick={() => setViewKey(v.key)}
                        className={`kp-quizview-btn${viewKey === v.key ? ' active' : ''}`}
                    >
                        {v.label}
                    </button>
                ))}
            </div>

            {/* GRE only: nested book-source sub-tabs for the active view. */}
            {hasSources && (
                <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2rem' }}>
                    {sourceTabs.map(st => (
                        <button
                            key={st.source}
                            type="button"
                            onClick={() => setSourceKey(st.source)}
                            onMouseEnter={() => setHoveredSource(st.source)}
                            onMouseLeave={() => setHoveredSource(null)}
                            style={sourceTabStyle(sourceKey === st.source, hoveredSource === st.source)}
                        >
                            {st.label}
                        </button>
                    ))}
                </div>
            )}

            {view.mode === 'practice' ? (
                <PracticeView key={`${apiExam}-${view.key}-${source}`} exam={apiExam} source={source} />
            ) : (
                <WordListView key={`${apiExam}-${view.key}-${source}`} exam={apiExam} source={source} favoritesOnly={view.mode === 'favorites'} />
            )}
        </div>
    );
}
